/**
 * SessionScreen - signed in, not yet in a session: rejoin, create or join.
 *
 * HOSTING IS NOT FOR EVERYONE, and the server is the one enforcing it: a
 * player's identity may join a session and never create one. Offering the
 * button anyway sent them into a rules refusal that arrived as a bare 400;
 * the commonest way in is having played as a player in this browser before
 * (that identity persists, and it is not yours as GM), so the screen says
 * how to sign in as GM instead. Extracted from App; the markup is unchanged.
 */
import { backend } from '../../backend/app';
import clsx from '../../utils/clsx';
import styles from '../../styles/App.module.css';

/** The rejoin banner for a stored session the server still has. */
function RejoinBanner({ pendingRejoin, rejoining, onRejoin, onDismiss }) {
  return (
    <div className={styles.rejoinSection}>
      <p className={styles.rejoinText}>
        You were in session <b>{pendingRejoin.code}</b>
        {pendingRejoin.isGm ? ' (as GM)' : ' (as Player)'}.
      </p>
      <div className={styles.rejoinActions}>
        <button className={styles.btnPrimary} onClick={onRejoin} disabled={rejoining}>
          {rejoining ? 'Rejoining...' : 'Rejoin Session'}
        </button>
        <button className={styles.btnSecondary} onClick={onDismiss}>
          Start Fresh
        </button>
      </div>
    </div>
  );
}

/** Create a session - or, for a player identity, why not and what to do. */
function CreateSection({ canHost, sessionBusy, onCreate }) {
  if (!canHost) {
    return (
      <div className={styles.createSection}>
        <p className={styles.rejoinText}>
          You are signed in as a player, so you can join a game but not host one. Sign out and choose{' '}
          <b>Sign in as GM</b> to start a session.
        </p>
        <button className={styles.btnSignOut} onClick={() => backend.auth.signOut()} disabled={sessionBusy}>
          Sign Out
        </button>
      </div>
    );
  }
  return (
    <div className={styles.createSection}>
      <button className={styles.btnPrimary} onClick={onCreate} disabled={sessionBusy}>
        {sessionBusy ? 'Connecting…' : 'Create New Session (GM)'}
      </button>
    </div>
  );
}

/** Join by code: typed, or carried by the invite link. Enter joins. */
function JoinSection({ joinCodeInput, setJoinCodeInput, sessionBusy, onJoin }) {
  return (
    <div className={styles.joinSection}>
      <input
        className={styles.inputCode}
        placeholder="CODE"
        maxLength={6}
        value={joinCodeInput}
        onChange={(e) => setJoinCodeInput(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') onJoin();
        }}
      />
      <button className={styles.btnSecondary} onClick={onJoin} disabled={sessionBusy}>
        {sessionBusy ? 'Connecting…' : 'Join Session'}
      </button>
    </div>
  );
}

/**
 * @param {object} p - user, displayName, setDisplayName, pendingRejoin,
 *   rejoining, onRejoin, onDismissRejoin, sessionBusy, onCreate,
 *   joinCodeInput, setJoinCodeInput, onJoin, onAssets (a GM's library; hosts only).
 */
export default function SessionScreen(p) {
  return (
    <div className={styles.sessionScreen}>
      <h1 className={clsx(styles.title, styles.titleSession)}>Multiplayer Session</h1>
      <p className={styles.welcomeText}>Welcome!</p>
      {p.pendingRejoin && (
        <RejoinBanner
          pendingRejoin={p.pendingRejoin}
          rejoining={p.rejoining}
          onRejoin={p.onRejoin}
          onDismiss={p.onDismissRejoin}
        />
      )}
      <div className={styles.nameSection}>
        <label className={styles.nameLabel} htmlFor="session-display-name">
          Your Display Name
        </label>
        <input
          id="session-display-name"
          className={styles.inputField}
          value={p.displayName}
          onChange={(e) => p.setDisplayName(e.target.value)}
          placeholder="Enter your name..."
        />
      </div>
      <CreateSection canHost={p.user.canHost !== false} sessionBusy={p.sessionBusy} onCreate={p.onCreate} />
      <div className={styles.dividerText}>&mdash; OR &mdash;</div>
      <JoinSection
        joinCodeInput={p.joinCodeInput}
        setJoinCodeInput={p.setJoinCodeInput}
        sessionBusy={p.sessionBusy}
        onJoin={p.onJoin}
      />
      <div className={styles.stackedActions}>
        {p.user.canHost !== false && (
          <button className={styles.btnSecondary} data-testid="open-assets" onClick={p.onAssets}>
            Assets library
          </button>
        )}
        <button className={styles.btnSignOut} onClick={() => backend.auth.signOut()}>
          Sign Out
        </button>
      </div>
    </div>
  );
}
